import { Autocomplete, TextField } from "@mui/material";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTextDomainContext } from "@/app/providers";
import { isEqual } from "lodash";
import md5 from "md5";
import { AutocompleteDropdownProps } from "./AutocompleteDropdown.types";
import { DropdownOption } from "@/types";
import { getOptionLabel, getOptionValue } from "./helpers";

export const AutocompleteDropdown = (props: AutocompleteDropdownProps) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const optionList = useMemo(() => {
    return props.options || [];
  }, [props.options]);

  const optionAll = useMemo(() => {
    return [
      {
        value: "all",
        label: gettext("All"),
      },
    ];
  }, [gettext]);

  const componentLabel = useMemo(() => {
    return props.componentLabel || "";
  }, [props.componentLabel]);

  const showAll = useMemo(() => {
    return props.showAll || false;
  }, [props.showAll]);

  const allPosition = useMemo(() => {
    return props.allPosition || "start";
  }, [props.allPosition]);

  const options = useMemo(() => {
    if (!showAll) return optionList;

    // Add "All" at the end or start based on allPosition prop
    return allPosition === "start"
      ? [...optionAll, ...optionList]
      : [...optionList, ...optionAll];
  }, [allPosition, optionAll, optionList, showAll]);

  const selectSize = useMemo(() => {
    return props.size || "small";
  }, [props.size]);

  const defaultValue = useMemo(() => {
    return props.defaultValue || null;
  }, [props.defaultValue]);

  const normalizedDefaultValue = useMemo(() => {
    return Array.isArray(defaultValue)
      ? (defaultValue[0] ?? null)
      : defaultValue;
  }, [defaultValue]);

  let cacheKey = "AutocompleteDropdown";
  let componentId = "autocomplete-dropdown";
  if (props.componentLabel) {
    cacheKey += `-${props.componentLabel}`;
  }

  if (props.cacheKey) {
    cacheKey += `-${props.cacheKey}`;
  }

  componentId += "-" + md5(cacheKey);

  // Read from localStorage on initialization
  const getInitialValue = () => {
    try {
      const cachedValue = localStorage.getItem(cacheKey);
      if (cachedValue) {
        return JSON.parse(cachedValue);
      }
    } catch {
      // If parsing fails, ignore and return null
    }
    return null;
  };

  const [newSelectedValue, setNewSelectedValue] =
    useState<DropdownOption | null>(getInitialValue);
  const prevDefaultValueRef = useRef<unknown>(defaultValue);

  // Reset newSelectedValue when defaultValue changes
  useEffect(() => {
    if (!isEqual(prevDefaultValueRef.current, defaultValue)) {
      prevDefaultValueRef.current = defaultValue;
      setNewSelectedValue(null);
    }
  }, [defaultValue]);

  const selectedValue = useMemo(() => {
    let returnValue = null;

    // If user has manually selected a value, use it
    if (newSelectedValue) {
      // Verify the manually selected value still exists in options
      const stillExists = options.find(
        (x) => getOptionValue(x) === newSelectedValue.value,
      );
      if (stillExists) {
        returnValue = newSelectedValue;
      }
    }

    // If no manual selection or it doesn't exist anymore, use defaultValue
    if (!returnValue && defaultValue) {
      const defaultValueOnly = getOptionValue(normalizedDefaultValue);
      const defaultIndex = options.findIndex(
        (x) => getOptionValue(x) === defaultValueOnly,
      );
      if (defaultIndex > -1) {
        returnValue = options[defaultIndex];
      }
    }

    // If still no value and required, use first option
    if (!returnValue && options.length && props.required) {
      returnValue = options[0];
    }

    return returnValue;
  }, [newSelectedValue, normalizedDefaultValue, options, props.required]);

  const lastEmittedValueRef = useRef<unknown>(undefined);
  const onChange = props.onChange;

  useEffect(() => {
    const defaultValueOnly = getOptionValue(normalizedDefaultValue);
    if (typeof onChange === "function" && selectedValue?.value) {
      if (
        !isEqual(defaultValueOnly, selectedValue.value) &&
        !isEqual(lastEmittedValueRef.current, selectedValue.value)
      ) {
        lastEmittedValueRef.current = selectedValue.value;
        onChange(selectedValue);
      }
    }
  }, [normalizedDefaultValue, onChange, selectedValue]);

  useEffect(() => {
    if (!options.length) return;
    // Calculate the maximum width of options
    const maxWidth = Math.max(
      ...options.map((option) => getOptionLabel(option).length),
    );

    // Set a dynamic width based on the maximum label length
    const dynamicWidth = maxWidth * 5 + 100; // Adjust the multiplier and offset as needed

    // Apply the dynamic width to the Autocomplete element
    const autocompleteElement = document.getElementById(componentId);
    if (autocompleteElement) {
      autocompleteElement.style.width = `${dynamicWidth}px`;
    }
  }, [componentId, options]);

  const handleChange = (event: any, value: any) => {
    if (cacheKey && value) {
      localStorage.setItem(cacheKey, JSON.stringify(value));
    }
    setNewSelectedValue(value);
    if (!isEqual(value, props.defaultValue)) {
      props.onChange(value);
    }
  };

  return (
    <Autocomplete
      id={componentId}
      size={selectSize}
      options={options}
      getOptionDisabled={(option: DropdownOption) => option.disabled}
      autoHighlight
      sx={{
        ...props.sx,
      }}
      value={selectedValue}
      onChange={handleChange}
      isOptionEqualToValue={(option: DropdownOption, value: DropdownOption) => {
        if (option.value === value.value) {
          return true;
        }
        return false;
      }}
      disableClearable
      renderInput={(params) => (
        <TextField
          {...params}
          required={props.required}
          label={componentLabel}
          placeholder={componentLabel}
        />
      )}
    />
  );
};
